/**
 * Model description output tab — why each run in the schedule landed where it
 * did.
 *
 * The content is produced by the engine (`scheduler::decision_trace`): after a
 * run, the API re-decodes the reported answer with a recorder on and writes
 * `Week{n}/ModelDescription.json`, which SchedulerService passes through
 * untouched. The wording lives in the engine so the harness and this tab can
 * never disagree; this file only lays it out, filters it and sorts it.
 *
 * What it can and cannot explain is stated in the tab itself: the ORDER the
 * search chose is a searched result with no reasoning behind it, so the tab
 * explains, given that order, why each run could not start earlier and why it
 * used the line it did.
 *
 * Scope (v1): the default answer only, and not mid-week reschedules — both
 * read as "no description" here, with a sentence saying why.
 *
 * Matching the schedule (v2): the engine shows every run at its FINAL times
 * (after post-search compaction), lists any row it has no record of, and
 * checks itself against the rows it explains (`reconciliation`). This tab
 * then checks again against the rows actually on screen, because those can
 * change after the description was written — a cell edit, an upload, or a run
 * whose description failed to write. Either check failing shows a banner and
 * flags the runs involved, so a mismatch is never left for the planner to
 * spot by eye.
 */
import { memo, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { Box } from '@mui/material'
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { request } from 'Features/auth/axios-client'
import {
  ScheduleVariantContext,
  useIsOwnAnswer,
} from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { FONT_MONO, FONT_SANS, PanelHead, TOKENS, TYPE } from './SchedulerDesign'
import { TLSearchBox } from './SchedulerFilterSelect'

// ─── Wire types (mirror scheduler::decision_trace::ModelDescription) ─────────

export type MDStep = {
  from: number
  to: number
  hours: number
  category: string
  text: string
}

export type MDAlternative = { line: string; could_start: number; text: string }

export type MDPlacement = {
  seq: number
  visit: number
  kind: 'production' | 'joined' | 'carried_over' | 'trial' | 'gap_fill' | 'unexplained'
  product_code: string
  product_name: string
  base_code: string
  base_name: string
  kitchen: string
  line: string
  quantity: number
  kitchen_start: number
  kitchen_end: number
  line_start: number
  line_end: number
  headline: string
  main_cause: string
  main_category: string | null
  waited_hours: number
  steps: MDStep[]
  alternatives: MDAlternative[]
  stop: string
  cleans: string[]
  compaction: string | null
  /** The decoded times, when compaction moved the run after the search; the
   *  times above are always the final ones. Absent on v1 descriptions. */
  decoded?: { kitchen_start: number; kitchen_end: number; line_start: number; line_end: number } | null
  /** False for a run the description has but the schedule does not. Absent
   *  on v1 descriptions, which read as true. */
  in_schedule?: boolean
}

export type MDReconciliation = {
  checked: boolean
  exact: boolean
  rows: number
  placements: number
  matched: number
  moved: number
  issues: { kind: string; text: string }[]
}

export type MDCategoryHours = {
  category: string
  label: string
  hours: number
  placements: number
}

export type ModelDescription = {
  version: number
  consolidated: boolean
  summary: {
    placement_count: number
    base_count: number
    lines: string[]
    wait_by_category: MDCategoryHours[]
    finishing_placement: number | null
    compacted_count: number
  }
  placements: MDPlacement[]
  dropped: {
    product_code: string
    product_name: string
    quantity: number
    text: string
  }[]
  notices: { severity: string; message: string }[]
  /** The engine's own check against the rows it described. Absent on v1. */
  reconciliation?: MDReconciliation
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const fetchModelDescription = async (
  scheduleId: string,
  iteration: string,
  week?: number
): Promise<ModelDescription | null> => {
  const qs = week === undefined ? '' : `?week=${week}`
  const response = await request({
    url: `/schedulerView/${scheduleId}/${iteration}/model-description${qs}`,
    method: 'GET',
  })
  return (response?.data?.data as ModelDescription) ?? null
}

/** Keyed under `['scheduler', id, iter]` so the invalidation a finished run
 *  already does (ScheduleControlPanel / SchedulePackagingRoutes) refreshes it. */
export const useModelDescription = (
  scheduleId: string,
  iteration: string,
  week?: number
) =>
  useQuery(
    ['scheduler', scheduleId, iteration, 'model-description', week],
    () => fetchModelDescription(scheduleId, iteration, week),
    { enabled: !!scheduleId && !!iteration, staleTime: 60_000 }
  )

// ─── Presentation helpers ─────────────────────────────────────────────────────

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Same format as the engine's `fmt_hour`, so columns match the sentences. */
const fmtHour = (h: number): string => {
  const hh = Math.max(0, Math.floor(h))
  const week = Math.floor(hh / 168)
  const inWeek = hh % 168
  const s = `${DAYS[Math.floor(inWeek / 24)]} ${String(inWeek % 24).padStart(2, '0')}:00`
  return week === 0 ? s : `Wk${week + 1} ${s}`
}

const CATEGORY_COLOR: Record<string, string> = {
  kitchen_busy: TOKENS.teal300,
  line_busy: TOKENS.brandBlue,
  clean: TOKENS.ganttClean,
  prep: TOKENS.ganttPreClean,
  downtime: TOKENS.ganttDowntime,
  changeover: TOKENS.ganttChangeover,
  retry: TOKENS.statePending,
  transfer: TOKENS.line300,
  start_window: TOKENS.ink400,
  tank: TOKENS.stateComplete,
  temperature: TOKENS.amberFg,
  resource: TOKENS.ganttMaxRunClean,
  crew: TOKENS.stateRunning,
  hall: TOKENS.stateRunning,
  usage_group: TOKENS.stateRunning,
  kitchen_capacity: TOKENS.blue600,
  other: TOKENS.ink300,
}
const catColor = (c: string | null | undefined) =>
  (c && CATEGORY_COLOR[c]) || TOKENS.ink300

const KIND_LABEL: Record<MDPlacement['kind'], string | null> = {
  production: null,
  joined: 'Joined',
  carried_over: 'Carried over',
  trial: 'Trial',
  gap_fill: 'Gap fill',
  unexplained: 'No decision record',
}

// ─── Matching against the schedule on screen ────────────────────────────────

/** A run's identity as the schedule view shows it. Hours are whole in the
 *  engine and rounded to 2dp on output, so rounding makes the two agree. */
const runKey = (
  product: string,
  line: string,
  kitchen: string,
  ks: number,
  ke: number,
  ls: number,
  le: number
) => [product, line, kitchen, Math.round(ks), Math.round(ke), Math.round(ls), Math.round(le)].join('|')
const placementKey = (p: MDPlacement) =>
  runKey(p.product_code, p.line, p.kitchen, p.kitchen_start, p.kitchen_end, p.line_start, p.line_end)
const rowKey = (r: InputRow) =>
  runKey(
    r.productCode,
    r.line,
    r.kitchen,
    r.kitchen_start_time,
    r.kitchen_end_time,
    r.line_start_time,
    r.line_end_time
  )
/** Output quantities are rounded to 2dp; allow for that and nothing more. */
const sameQuantity = (a: number, b: number) => Math.abs(Number(a) - Number(b)) <= 0.011

/** A production bar selected on the schedule timeline, as the Model
 *  description filters by it.
 *
 *  A LINE bar is one packing run — product code on a line. A KITCHEN bar is
 *  one cook of a base material, which may feed several products and lines, so
 *  it selects every placement made from that cook. `start`/`end` are ABSOLUTE
 *  horizon hours (the description's frame); `barStart`/`barEnd` are the bar's
 *  own week-rebased hours, kept so the timeline can outline the same bar. */
export type MDRunFocus = {
  stage: 'pack' | 'base'
  /** Line name for a line bar, kitchen name for a kitchen bar. */
  resource: string
  /** Product code for a line bar, base material code for a kitchen bar. */
  code: string
  start: number
  end: number
  barStart: number
  barEnd: number
  /** What the chip on the tab calls the selection. */
  label: string
}

/** Overlap rather than equality: a bar cut at a week boundary is clamped to
 *  the week, so its hours are not the run's own, but it still overlaps it. */
const overlaps = (s: number, e: number, fs: number, fe: number) =>
  s === e || fs === fe ? s <= fe && e >= fs : s < fe && e > fs

export const matchesFocus = (p: MDPlacement, f: MDRunFocus): boolean =>
  f.stage === 'pack'
    ? p.line === f.resource && p.product_code === f.code && overlaps(p.line_start, p.line_end, f.start, f.end)
    : p.kitchen === f.resource &&
      p.base_code === f.code &&
      overlaps(p.kitchen_start, p.kitchen_end, f.start, f.end)

export type ScreenCheck = {
  /** Placements shown for the week on screen. */
  placements: MDPlacement[]
  /** seq of each shown placement that does not match a row on screen, and why. */
  flagged: Map<number, string>
  /** Rows on screen that no placement describes. */
  undescribedRows: InputRow[]
}

/** Pair the description's placements one-for-one with the rows on screen.
 *
 *  For a consolidated description the week is defined by the rows on screen
 *  (a placement is shown when it matches one), not by a time window: the
 *  schedule buckets a row into a week on its whole activity span, cleans
 *  included, so a window on line times alone disagrees with it at the week
 *  edges. A consolidated placement that matches nothing is shown only if it
 *  overlaps the week, so a genuinely missing run is still flagged. */
export const checkAgainstScreen = (
  d: ModelDescription,
  rows: InputRow[],
  weekBaseHour: number | null
): ScreenCheck => {
  const pool = new Map<string, InputRow[]>()
  for (const r of rows) {
    const k = rowKey(r)
    const list = pool.get(k)
    if (list) list.push(r)
    else pool.set(k, [r])
  }
  const flagged = new Map<number, string>()
  const placements: MDPlacement[] = []
  const inWeek = (p: MDPlacement) =>
    !d.consolidated ||
    weekBaseHour == null ||
    (p.line_end > weekBaseHour && p.line_start < weekBaseHour + 168)
  for (const p of d.placements) {
    const list = pool.get(placementKey(p))
    const hit = list ? list.findIndex((r) => sameQuantity(r.quantity, p.quantity)) : -1
    if (list && hit >= 0) {
      list.splice(hit, 1)
      placements.push(p)
      if (p.in_schedule === false) {
        flagged.set(p.seq, 'The engine reported this run as not in the schedule.')
      }
      continue
    }
    if (!inWeek(p)) continue
    placements.push(p)
    let why = 'Described, but not on the schedule shown — it has changed since this description was written.'
    if (list && list.length) why = 'On screen with a different quantity than described.'
    else if (p.in_schedule === false) why = 'Described, but not in the schedule the run reported.'
    flagged.set(p.seq, why)
  }
  const undescribedRows = Array.from(pool.values()).flat()
  return { placements, flagged, undescribedRows }
}

type SortKey = 'order' | 'waited' | 'start'

// ─── Placement row ────────────────────────────────────────────────────────────

const StepChain = memo(({ steps }: { steps: MDStep[] }) => {
  const total = steps.reduce((s, x) => s + x.hours, 0)
  if (!steps.length) return null
  return (
    <Box sx={{ mb: '10px' }}>
      {/* Proportional bar: the whole wait at a glance, coloured by cause. */}
      <Box
        sx={{
          display: 'flex',
          height: 8,
          borderRadius: '2px',
          overflow: 'hidden',
          bgcolor: TOKENS.ganttIdle,
          mb: '8px',
        }}
      >
        {steps.map((s, i) => (
          <Box
            key={i}
            title={s.text}
            sx={{
              flex: `${Math.max(s.hours, 0.001)} 0 0`,
              bgcolor: catColor(s.category),
              borderRight: i < steps.length - 1 ? '1px solid #fff' : 'none',
            }}
          />
        ))}
      </Box>
      {steps.map((s, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: '10px 150px 44px 1fr',
            gap: '8px',
            alignItems: 'baseline',
            py: '2px',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '2px',
              bgcolor: catColor(s.category),
              alignSelf: 'center',
            }}
          />
          <Box sx={{ ...TYPE.mono, fontSize: 11, color: TOKENS.ink500 }}>
            {fmtHour(s.from)} → {fmtHour(s.to)}
          </Box>
          <Box sx={{ ...TYPE.mono, fontSize: 11, color: TOKENS.ink700, textAlign: 'right' }}>
            {s.hours}h
          </Box>
          <Box sx={{ ...TYPE.small, color: TOKENS.ink700 }}>{s.text}</Box>
        </Box>
      ))}
      {total > 0 && (
        <Box sx={{ ...TYPE.micro, mt: '4px' }}>{total}h from hour 0 to this start</Box>
      )}
    </Box>
  )
})
StepChain.displayName = 'StepChain'

const DetailBlock = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: '10px' }}>
    <Box sx={{ ...TYPE.micro, mb: '4px' }}>{label}</Box>
    {children}
  </Box>
)

const PlacementRow = memo(
  ({
    p,
    open,
    onToggle,
    isFinishing,
    flag,
  }: {
    p: MDPlacement
    open: boolean
    onToggle: () => void
    isFinishing: boolean
    /** Why this run does not match the schedule on screen, when it does not. */
    flag?: string
  }) => {
    const kind = KIND_LABEL[p.kind]
    return (
      <Box sx={{ borderBottom: `1px solid ${TOKENS.line100}` }}>
        <Box
          component="button"
          onClick={onToggle}
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            width: '100%',
            cursor: 'pointer',
            display: 'grid',
            gridTemplateColumns: '18px 44px minmax(160px, 1.3fr) 110px 190px 64px minmax(220px, 2fr)',
            alignItems: 'center',
            gap: '10px',
            px: '14px',
            py: '7px',
            bgcolor: open ? TOKENS.surface50 : 'transparent',
            '&:hover': { bgcolor: TOKENS.surface50 },
            fontFamily: FONT_SANS,
          }}
        >
          <Box sx={{ color: TOKENS.ink400, display: 'flex' }}>
            {open ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </Box>
          <Box sx={{ ...TYPE.mono, fontSize: 11, color: TOKENS.ink400 }}>#{p.seq}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: TOKENS.ink900,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {p.product_name}
            </Box>
            <Box sx={{ fontSize: 10, color: TOKENS.ink400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.product_code}
              {kind ? ` · ${kind}` : ''}
              {isFinishing ? ' · finishes the schedule' : ''}
            </Box>
            {flag && (
              <Box sx={{ fontSize: 10, fontWeight: 600, color: TOKENS.amberFg }} title={flag}>
                Does not match the schedule
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ ...TYPE.mono, fontSize: 11.5, color: TOKENS.ink700 }}>{p.line}</Box>
            <Box sx={{ ...TYPE.mono, fontSize: 10, color: TOKENS.ink400 }}>← {p.kitchen}</Box>
          </Box>
          <Box sx={{ ...TYPE.mono, fontSize: 11, color: TOKENS.ink600 }}>
            {fmtHour(p.line_start)} → {fmtHour(p.line_end)}
          </Box>
          <Box
            sx={{
              ...TYPE.mono,
              fontSize: 11,
              fontWeight: 600,
              textAlign: 'center',
              borderRadius: '2px',
              py: '1px',
              color: p.waited_hours ? '#fff' : TOKENS.ink400,
              bgcolor: p.waited_hours ? catColor(p.main_category) : TOKENS.ganttIdle,
            }}
            title={p.main_category ?? undefined}
          >
            {p.waited_hours}h
          </Box>
          <Box
            sx={{
              ...TYPE.small,
              color: TOKENS.ink700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: open ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {p.main_cause}
            {p.compaction && (
              <Box component="span" sx={{ color: TOKENS.stateComplete }}> {p.compaction}</Box>
            )}
          </Box>
        </Box>
        {open && (
          <Box sx={{ pl: '86px', pr: '18px', pt: '4px', pb: '14px', bgcolor: TOKENS.surface50 }}>
            <Box sx={{ ...TYPE.small, color: TOKENS.ink600, mb: '10px' }}>{p.headline}</Box>
            {flag && <Box sx={{ ...TYPE.small, color: TOKENS.amberFg, mb: '10px' }}>{flag}</Box>}
            {p.steps.length > 0 && (
              <DetailBlock label="What held it back">
                <StepChain steps={p.steps} />
              </DetailBlock>
            )}
            {p.alternatives.length > 0 && (
              <DetailBlock label="Other lines considered">
                {p.alternatives.map((a, i) => (
                  <Box key={i} sx={{ ...TYPE.small, color: TOKENS.ink700, py: '1px' }}>
                    · {a.text}
                  </Box>
                ))}
              </DetailBlock>
            )}
            {p.cleans.length > 0 && (
              <DetailBlock label="Cleans booked with this run">
                {p.cleans.map((c, i) => (
                  <Box key={i} sx={{ ...TYPE.small, color: TOKENS.ink700, py: '1px' }}>
                    · {c}
                  </Box>
                ))}
              </DetailBlock>
            )}
            {p.stop && (
              <DetailBlock label="Why it stopped">
                <Box sx={{ ...TYPE.small, color: TOKENS.ink700 }}>{p.stop}</Box>
              </DetailBlock>
            )}
            <Box sx={{ ...TYPE.micro, textTransform: 'none', letterSpacing: 0 }}>
              Base {p.base_name} ({p.base_code}) · {Math.round(p.quantity).toLocaleString()} units · kitchen{' '}
              {fmtHour(p.kitchen_start)} → {fmtHour(p.kitchen_end)} · base #{p.visit} in the search's order
            </Box>
          </Box>
        )}
      </Box>
    )
  }
)
PlacementRow.displayName = 'PlacementRow'

// ─── Tab ──────────────────────────────────────────────────────────────────────

export const ModelDescriptionTab = memo(
  ({
    scheduleId,
    iteration,
    selectedWeek,
    weekBaseHour = null,
    rows,
    focus = null,
    onClearFocus,
  }: {
    scheduleId: string
    iteration: string
    selectedWeek?: number
    /** Absolute-horizon offset of the week on screen; `null` for a
     *  single-week schedule. Used to filter a consolidated description. */
    weekBaseHour?: number | null
    /** The schedule rows on screen for this week, in the same (absolute)
     *  hours as the description. When given, every placement is checked
     *  against them; without it the tab falls back to a time-window filter
     *  and can only show the engine's own check. */
    rows?: InputRow[]
    /** A run selected on the timeline: the list narrows to it and opens it. */
    focus?: MDRunFocus | null
    onClearFocus?: () => void
  }) => {
    const scheduleVariant = useContext(ScheduleVariantContext)
    const ownAnswer = useIsOwnAnswer()
    const { data, isLoading, isError } = useModelDescription(scheduleId, iteration, selectedWeek)
    const [search, setSearch] = useState('')
    const [category, setCategory] = useState<string | null>(null)
    const [onlyWaited, setOnlyWaited] = useState(false)
    const [sort, setSort] = useState<SortKey>('order')
    const [open, setOpen] = useState<Record<number, boolean>>({})
    const [showNotices, setShowNotices] = useState(false)
    const [showMismatch, setShowMismatch] = useState(false)

    const screen = useMemo(
      () => (data && rows ? checkAgainstScreen(data, rows, weekBaseHour) : null),
      [data, rows, weekBaseHour]
    )

    // A consolidated solve describes the whole horizon; show the week on screen.
    const weekPlacements = useMemo(() => {
      if (!data) return []
      if (screen) return screen.placements
      if (!data.consolidated || weekBaseHour == null) return data.placements
      const lo = weekBaseHour
      const hi = weekBaseHour + 168
      return data.placements.filter((p) => p.line_end > lo && p.line_start < hi)
    }, [data, screen, weekBaseHour])

    // The engine's check covers the whole description, so for a consolidated
    // run it is horizon-wide; the screen check is what this week can vouch for.
    const engineIssues =
      data?.reconciliation?.checked && !data.reconciliation.exact ? data.reconciliation.issues : []
    const screenIssueCount = screen ? screen.flagged.size + screen.undescribedRows.length : 0
    const mismatched = engineIssues.length > 0 || screenIssueCount > 0

    const focused = useMemo(
      () => (focus ? weekPlacements.filter((p) => matchesFocus(p, focus)) : null),
      [weekPlacements, focus]
    )

    // A new selection starts clean: an earlier search or cause filter would
    // otherwise hide the very run that was just clicked. The matches open, so
    // the explanation is on screen without a second click.
    useEffect(() => {
      if (!focus) return
      setSearch('')
      setCategory(null)
      setOnlyWaited(false)
      if (focused?.length) setOpen(Object.fromEntries(focused.map((p) => [p.seq, true])))
      // On a new selection, and again when the description arrives — "Explain"
      // can open this tab before its fetch has landed. Not on every
      // recomputation of `focused`, which would undo the planner's own clicks.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus, data])

    const visible = useMemo(() => {
      const q = search.trim().toLowerCase()
      const rows = (focused ?? weekPlacements).filter((p) => {
        if (onlyWaited && p.waited_hours <= 0) return false
        if (category && !p.steps.some((s) => s.category === category)) return false
        if (!q) return true
        return [p.product_name, p.product_code, p.base_name, p.base_code, p.line, p.kitchen, p.main_cause]
          .some((v) => v && v.toLowerCase().includes(q))
      })
      const sorted = rows.slice()
      if (sort === 'waited') sorted.sort((a, b) => b.waited_hours - a.waited_hours || a.seq - b.seq)
      else if (sort === 'start') sorted.sort((a, b) => a.line_start - b.line_start || a.seq - b.seq)
      return sorted
    }, [weekPlacements, focused, search, onlyWaited, category, sort])

    const totalWait = useMemo(
      () => weekPlacements.reduce((s, p) => s + p.waited_hours, 0),
      [weekPlacements]
    )
    const maxCatHours = Math.max(1, ...(data?.summary.wait_by_category.map((c) => c.hours) ?? [1]))

    const empty = (text: string) => (
      <Box sx={{ p: 4, textAlign: 'center', color: TOKENS.ink400, fontSize: 12.5, fontFamily: FONT_SANS }}>
        {text}
      </Box>
    )

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: TOKENS.surface0 }}>
        <PanelHead
          icon={<PsychologyAltIcon sx={{ fontSize: 19 }} />}
          title="Model description"
          sub="Why each run landed where it did"
          metrics={
            data
              ? [
                  { label: 'Runs', value: weekPlacements.length },
                  { label: 'Hours waiting', value: totalWait, unit: 'h' },
                  {
                    label: 'Moved after search',
                    value: data.summary.compacted_count,
                    tone: data.summary.compacted_count ? TOKENS.stateRunning : TOKENS.stateComplete,
                  },
                ]
              : []
          }
        />
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, fontFamily: FONT_SANS }}>
          {isLoading && empty('Loading the model description…')}
          {isError && empty('The model description could not be loaded.')}
          {!isLoading && !isError && !data &&
            empty(
              'No model description for this schedule yet. It is written when the scheduler runs, so re-run to create one. ' +
                'Mid-week reschedules do not have one yet.'
            )}
          {data && (
            <>
              {!ownAnswer && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    mx: '18px',
                    mt: '12px',
                    px: '12px',
                    py: '8px',
                    borderRadius: '3px',
                    bgcolor: TOKENS.blue50,
                    color: TOKENS.blue600,
                    ...TYPE.small,
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  This explains the run&apos;s default (Balanced) answer. You are viewing an alternative answer, whose
                  runs may differ.
                </Box>
              )}

              {mismatched && (
                <Box
                  sx={{
                    mx: '18px',
                    mt: '12px',
                    px: '12px',
                    py: '8px',
                    borderRadius: '3px',
                    bgcolor: 'rgba(224,146,47,0.12)',
                    color: TOKENS.amberFg,
                    ...TYPE.small,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    <Box sx={{ flex: 1 }}>
                      This description does not fully match the schedule
                      {screen
                        ? `: ${screen.flagged.size} described run(s) do not match the schedule shown, and ${screen.undescribedRows.length} run(s) on screen have no description.`
                        : '.'}
                      {screen && screenIssueCount > 0 && engineIssues.length === 0
                        ? ' The schedule has changed since this description was written (an edit, an upload, or a run whose description was not saved) — re-run to refresh it.'
                        : ''}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => setShowMismatch((v) => !v)}
                      sx={{ all: 'unset', cursor: 'pointer', fontWeight: 600 }}
                    >
                      {showMismatch ? 'Hide details' : 'Show details'}
                    </Box>
                  </Box>
                  {showMismatch && (
                    <Box sx={{ mt: '6px', pl: '24px' }}>
                      {screen?.undescribedRows.map((r, i) => (
                        <Box key={`r${i}`} sx={{ py: '1px' }}>
                          · On screen, not described: {r.productCode} on {r.line} (kitchen {r.kitchen}),{' '}
                          {fmtHour(r.line_start_time)} → {fmtHour(r.line_end_time)}, qty{' '}
                          {Number(r.quantity).toFixed(2)}
                        </Box>
                      ))}
                      {engineIssues.map((iss, i) => (
                        <Box key={`e${i}`} sx={{ py: '1px' }}>
                          · {iss.text}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Summary */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(280px, 1.2fr) minmax(260px, 1fr)',
                  gap: '18px',
                  px: '18px',
                  py: '14px',
                  borderBottom: `1px solid ${TOKENS.line200}`,
                }}
              >
                <Box>
                  {data.summary.lines.map((l, i) => (
                    <Box key={i} sx={{ ...TYPE.body, color: i === 0 ? TOKENS.ink600 : TOKENS.ink900, mb: '6px' }}>
                      {l}
                    </Box>
                  ))}
                  <Box sx={{ ...TYPE.small, color: TOKENS.ink400, mt: '8px' }}>
                    The order itself is what the search found to score best, so it has no reason to report. What is
                    explained here is why, given that order, each run could not start earlier, and why it used the
                    line it did.
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ ...TYPE.micro, mb: '6px' }}>Waiting by cause — click to filter</Box>
                  {data.summary.wait_by_category.map((c) => {
                    const active = category === c.category
                    return (
                      <Box
                        key={c.category}
                        component="button"
                        onClick={() => setCategory(active ? null : c.category)}
                        sx={{
                          all: 'unset',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: '170px 1fr 52px',
                          gap: '8px',
                          alignItems: 'center',
                          py: '3px',
                          px: '4px',
                          borderRadius: '2px',
                          bgcolor: active ? TOKENS.tealTint : 'transparent',
                          '&:hover': { bgcolor: TOKENS.surface50 },
                        }}
                        title={`Main reason for ${c.placements} runs`}
                      >
                        <Box sx={{ ...TYPE.small, color: TOKENS.ink700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.label}
                        </Box>
                        <Box sx={{ height: 8, bgcolor: TOKENS.ganttIdle, borderRadius: '2px', overflow: 'hidden' }}>
                          <Box sx={{ width: `${(c.hours / maxCatHours) * 100}%`, height: '100%', bgcolor: catColor(c.category) }} />
                        </Box>
                        <Box sx={{ ...TYPE.mono, fontSize: 11, textAlign: 'right', color: TOKENS.ink600 }}>{c.hours}h</Box>
                      </Box>
                    )
                  })}
                  {data.notices.length > 0 && (
                    <Box sx={{ mt: '10px' }}>
                      <Box
                        component="button"
                        onClick={() => setShowNotices((v) => !v)}
                        sx={{ all: 'unset', cursor: 'pointer', ...TYPE.micro, color: TOKENS.stateRunning }}
                      >
                        {showNotices ? 'Hide' : 'Show'} {data.notices.length} data notices from this run
                      </Box>
                      {showNotices &&
                        data.notices.map((n, i) => (
                          <Box key={i} sx={{ ...TYPE.small, color: TOKENS.ink700, py: '2px' }}>
                            <Box component="span" sx={{ ...TYPE.mono, fontSize: 10, color: TOKENS.ink400, mr: '6px' }}>
                              {n.severity}
                            </Box>
                            {n.message}
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {focus && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    px: '18px',
                    py: '8px',
                    bgcolor: TOKENS.tealTint,
                    borderBottom: `1px solid ${TOKENS.line200}`,
                    ...TYPE.small,
                    color: TOKENS.ink700,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    Showing the run selected on the schedule: <b>{focus.label}</b>
                    {focused && focused.length > 1 ? ` — ${focused.length} runs came from this cook` : ''}
                    {focused && focused.length === 0
                      ? ' — this description has no entry for it (see the mismatch note above, or re-run to refresh).'
                      : ''}
                  </Box>
                  {onClearFocus && (
                    <Box
                      component="button"
                      onClick={onClearFocus}
                      sx={{ all: 'unset', cursor: 'pointer', fontWeight: 600, color: TOKENS.brandTeal }}
                    >
                      Show all runs ✕
                    </Box>
                  )}
                </Box>
              )}

              {/* Controls */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  px: '18px',
                  py: '8px',
                  borderBottom: `1px solid ${TOKENS.line200}`,
                  flexWrap: 'wrap',
                }}
              >
                <TLSearchBox value={search} onChange={setSearch} placeholder="Filter product, line, kitchen…" width={230} />
                <Box
                  component="label"
                  sx={{ display: 'flex', alignItems: 'center', gap: '5px', ...TYPE.small, color: TOKENS.ink600, cursor: 'pointer' }}
                >
                  <input type="checkbox" checked={onlyWaited} onChange={(e) => setOnlyWaited(e.target.checked)} />
                  Only runs that waited
                </Box>
                {category && (
                  <Box
                    component="button"
                    onClick={() => setCategory(null)}
                    sx={{
                      all: 'unset',
                      cursor: 'pointer',
                      ...TYPE.small,
                      px: '8px',
                      py: '2px',
                      borderRadius: '2px',
                      bgcolor: TOKENS.tealTint,
                      color: TOKENS.brandTeal,
                    }}
                  >
                    {data.summary.wait_by_category.find((c) => c.category === category)?.label ?? category} ✕
                  </Box>
                )}
                <Box sx={{ flex: 1 }} />
                <Box sx={{ ...TYPE.micro }}>Sort</Box>
                {(
                  [
                    ['order', 'Order placed'],
                    ['start', 'Start time'],
                    ['waited', 'Most waiting'],
                  ] as const
                ).map(([k, label]) => (
                  <Box
                    key={k}
                    component="button"
                    onClick={() => setSort(k)}
                    sx={{
                      border: `1px solid ${sort === k ? TOKENS.stateComplete : TOKENS.line300}`,
                      borderRadius: '2px',
                      px: '8px',
                      height: 24,
                      cursor: 'pointer',
                      bgcolor: sort === k ? 'rgba(14,124,134,0.08)' : '#fff',
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: sort === k ? TOKENS.stateComplete : TOKENS.ink500,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>

              {/* Column heads */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '18px 44px minmax(160px, 1.3fr) 110px 190px 64px minmax(220px, 2fr)',
                  gap: '10px',
                  px: '14px',
                  py: '6px',
                  bgcolor: TOKENS.panelHead,
                  borderBottom: `1px solid ${TOKENS.line200}`,
                  ...TYPE.micro,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Box />
                <Box>#</Box>
                <Box>Product</Box>
                <Box>Line ← kitchen</Box>
                <Box>Runs</Box>
                <Box sx={{ textAlign: 'center' }}>Waited</Box>
                <Box>Main reason</Box>
              </Box>

              {visible.length === 0
                ? empty('No runs match the filter.')
                : visible.map((p) => (
                    <PlacementRow
                      key={`${p.seq}-${p.line}`}
                      p={p}
                      open={!!open[p.seq]}
                      onToggle={() => setOpen((o) => ({ ...o, [p.seq]: !o[p.seq] }))}
                      isFinishing={data.summary.finishing_placement === p.seq}
                      flag={screen?.flagged.get(p.seq)}
                    />
                  ))}

              {data.dropped.length > 0 && (
                <Box sx={{ px: '18px', py: '14px' }}>
                  <Box sx={{ ...TYPE.micro, mb: '6px' }}>Small remainders not made</Box>
                  {data.dropped.map((d, i) => (
                    <Box key={i} sx={{ ...TYPE.small, color: TOKENS.ink700, py: '2px' }}>
                      · {d.text}
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ height: 16, fontFamily: FONT_MONO }} />
            </>
          )}
        </Box>
      </Box>
    )
  }
)
ModelDescriptionTab.displayName = 'ModelDescriptionTab'
